var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import { withDone, withValidate } from '@rad/utility/http';
import { navigate, requireToken } from '../../../utility/general';
import { postQuickQuote, fetchClient } from '../../../http/invoices';
import { getConnectorPartsTech, getVehicleByVin, getVehicleByPlate } from '../../../http/connectors';
import CModal from '../modal';
import CModalConfirm from '../modal.confirm';
import CInput from '../input';
import CSelect from '../select';
import CSelectState from '../select.state';
import Button from '../button';
import FormClient from '../../forms/client';
const fullName = (x) => `${x.firstname} ${x.lastname}`.trim();
export default (props) => {
    const token = requireToken('merchant');
    const [client, setClient] = React.useState(null);
    const [name, setName] = React.useState('');
    const [description, setDescription] = React.useState('');
    const [vin, setVin] = React.useState('');
    const [plate, setPlate] = React.useState('');
    const [plateState, setPlateState] = React.useState(null);
    const [error, setError] = React.useState(null);
    const [partstech, setPartstech] = React.useState(null);
    const [lookup, setLookup] = React.useState(null);
    // Vehicles on file for the selected existing client. Populated lazily
    // when a client is picked so a phone-in quote can pre-fill from history
    // instead of retyping VIN/plate.
    const [vehicles, setVehicles] = React.useState([]);
    const [selectedVehicleId, setSelectedVehicleId] = React.useState(null);
    React.useEffect(() => {
        if (!props.visible)
            return;
        setClient(null);
        setName('');
        setDescription('');
        setVin('');
        setPlate('');
        setPlateState(null);
        setError(null);
        setLookup(null);
        setVehicles([]);
        setSelectedVehicleId(null);
    }, [props.visible]);
    React.useEffect(() => {
        getConnectorPartsTech(token.merchant.id).then(setPartstech).catch(() => setPartstech(null));
    }, [token.merchant.id]);
    // When an existing client is picked, load their vehicles so the caller
    // can pick one instead of typing. Manual client entries (no id) stay
    // vehicle-less.
    React.useEffect(() => {
        if (!client) {
            setVehicles([]);
            setSelectedVehicleId(null);
            return;
        }
        let cancelled = false;
        withDone(fetchClient(client.id))()
            .then((full) => { if (!cancelled)
            setVehicles(full.vehicles || []); })
            .catch(() => { if (!cancelled)
            setVehicles([]); });
        return () => { cancelled = true; };
    }, [client === null || client === void 0 ? void 0 : client.id]);
    const pickSavedVehicle = (id) => {
        setSelectedVehicleId(id);
        const v = vehicles.find((x) => x.id === id);
        if (!v) {
            // Clearing the selection resets the vehicle fields so the user can
            // type manually or lookup fresh.
            setDescription('');
            setVin('');
            setPlate('');
            return;
        }
        setDescription(v.description || '');
        setVin(v.vin || '');
        setPlate(v.tag || '');
        // Vehicle.state is not exposed on the client Vehicle type today
        // (server DAL doesn't SELECT it). Plate-based PartsTech lookup will
        // still require the user to pick State manually.
    };
    const doLookup = (x, done) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            if (!x)
                return;
            if (x.kind === 'vin') {
                const response = yield getVehicleByVin(token.merchant.id, x.vin);
                if (response) {
                    setDescription(response.name);
                    setVin(response.vin);
                    setLookup(null);
                }
                else {
                    window.alert('Lookup Failed - Verify the VIN is correct');
                }
            }
            else {
                const response = yield getVehicleByPlate(token.merchant.id, x.state || '', x.tag);
                if (response) {
                    setDescription(response.name);
                    setVin(response.vin);
                    setPlate(x.tag);
                    setPlateState(x.state);
                    setLookup(null);
                }
                else {
                    window.alert('Lookup Failed - verify the State and Tag/Plate are correct');
                }
            }
        }
        catch (_a) {
            window.alert('Lookup Failed - please verify entered values');
        }
        finally {
            done();
        }
    });
    const submit = (done) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        setError(null);
        if (!client && !name.trim()) {
            setError('Pick an existing customer or enter a name');
            done();
            return;
        }
        try {
            const result = yield withDone(withValidate(postQuickQuote(props.merchantId, {
                'client_id': (_a = client === null || client === void 0 ? void 0 : client.id) !== null && _a !== void 0 ? _a : null,
                'client_name': client ? fullName(client) : name.trim(),
                'vehicle_description': description.trim() || null,
                'vehicle_vin': vin.trim() || null,
                'vehicle_tag': plate.trim() || null,
                'vehicle_state': (plate.trim() && plateState) || null,
            })))();
            if (result[0]) {
                const data = result[1];
                navigate(`#/invoices/${data.merchant_id}/${data.id}/edit?openPartsTech=1`);
                props.onClose();
            }
            else {
                setError(((_b = result[1][0]) === null || _b === void 0 ? void 0 : _b.message) || 'Failed to create quick quote');
            }
        }
        finally {
            done();
        }
    });
    return _jsxs(_Fragment, { children: [_jsxs(CModal, { visible: props.visible, title: "Quick Quote", action: "Create & Open PartsTech", onClose: props.onClose, onSubmit: submit, children: [_jsx(FormClient, { nameonly: true, errors: {}, client: client, client_firstname: name, client_lastname: "", client_businessname: "", client_no_address: true, client_tax_exempt: false, client_address: "", client_address2: "", client_city: "", client_state: null, client_zip: "", client_contacts: [], client_notes: [], client_notes_new: [], onChange: () => { }, onChange_client: setClient, onChange_client_firstname: setName, onChange_client_lastname: () => { }, onChange_client_businessname: () => { }, onChange_client_no_address: () => { }, onChange_client_tax_exempt: () => { }, onChange_client_address: () => { }, onChange_client_address2: () => { }, onChange_client_city: () => { }, onChange_client_state: () => { }, onChange_client_zip: () => { }, onChange_client_contacts: () => { }, onChange_client_notes: () => { }, onChange_client_notes_new: () => { } }), !!client && vehicles.length > 0 && _jsx(CSelect, { small: true, items: vehicles, valuef: (x) => x.id, titlef: (x) => `${x.description || 'Vehicle'}${x.vin ? ` (${x.vin})` : x.tag ? ` (${x.tag})` : ''}`, value: selectedVehicleId, onChange: pickSavedVehicle, emptyTitle: "Enter vehicle manually", children: "Vehicle on file" }), !!partstech && _jsxs("div", { className: "d-flex mb-3", children: [_jsx(Button, { color: "primary", w100: true, me: 1, onClick: () => setLookup({ kind: 'plate', state: plateState, tag: plate }), children: "Lookup by Tag/Plate" }), _jsx(Button, { color: "primary", w100: true, ms: 1, onClick: () => setLookup({ kind: 'vin', vin: vin }), children: "Lookup by VIN" })] }), _jsx(CInput, { type: "text", small: true, value: description, onChange: setDescription, children: "Vehicle Type" }), _jsx(CInput, { type: "text", small: true, value: vin, onChange: setVin, children: "VIN" }), _jsxs("div", { className: "d-flex gap-2", children: [_jsx("div", { className: "flex-grow-1", children: _jsx(CInput, { type: "text", small: true, value: plate, onChange: setPlate, children: "Tag/Plate" }) }), _jsx("div", { style: { 'width': '140px' }, children: _jsx(CSelectState, { value: plateState, onChange: setPlateState, small: true, children: "State" }) })] }), !!error && _jsx("div", { className: "text-danger mb-2", children: error })] }), _jsxs(CModalConfirm, { title: "Lookup Vehicle", action: "Lookup", visible: !!lookup, level: 2, onClose: () => setLookup(null), onSubmit: (f) => doLookup(lookup, f), children: [(lookup === null || lookup === void 0 ? void 0 : lookup.kind) === 'vin' && _jsx(CInput, { type: "text", value: lookup.vin, onChange: (x) => setLookup({ kind: 'vin', vin: x }), required: true, autoFocus: true, children: "VIN" }), (lookup === null || lookup === void 0 ? void 0 : lookup.kind) === 'plate' && _jsxs(_Fragment, { children: [_jsx(CSelectState, { value: lookup.state, onChange: (x) => setLookup({ kind: 'plate', state: x, tag: lookup.tag }), required: true, children: "State" }), _jsx(CInput, { type: "text", value: lookup.tag, onChange: (x) => setLookup({ kind: 'plate', state: lookup.state, tag: x }), required: true, autoFocus: true, children: "Tag/Plate" })] })] })] });
};
